import find from 'lodash/find.js';
import first from 'lodash/first.js';
import uniqBy from 'lodash/uniqBy.js';

import SectionTemplate from '@/models/SectionTemplate.js';
import moment from '@/plugins/vue-moment.js';

export default {
    state: {
        timePeriods: [
            // {
            //     id: 1,
            //     enrollment_group_id: null,
            //     from: Date
            //     to: Date
            //     display_week_number: 1,
            // }
        ],
        editingTimePeriodId: null,
        courseKeyEnabled: false,
        checkInWindow: 0,
        checkOutWindow: 0,
    },

    getters: {
        debug(state) {
            return state.timePeriods
                .map(timePeriod => {
                    return {
                        id: timePeriod.id,
                        enrollment_group_id: timePeriod.enrollment_group_id,
                        from: timePeriod.from.format('LLL zz'),
                        to: timePeriod.to.format('LLL zz'),
                        window_from: timePeriod.window_from.format('LLL zz'),
                        window_to: timePeriod.window_to.format('LLL zz'),
                        display_week_number: timePeriod.display_week_number,
                    };
                })
                .sort((a, b) => a.from.valueOf() - b.from.valueOf());
        },

        findById(state) {
            return id => state.timePeriods.find(timePeriod => timePeriod.id === id);
        },

        findRangeById(state, getters) {
            return id => {
                const timePeriod = getters.findById(id);

                if (!timePeriod) {
                    return null;
                }

                return moment.range(timePeriod.from, timePeriod.to);
            };
        },

        isOverlapped(state, getters) {
            return timePeriodId => !!find(getters.overlappedTimePeriods, timePeriod => timePeriod.id === timePeriodId);
        },

        hasAnyOverlapped(state, getters) {
            return getters.overlappedTimePeriods.length > 0;
        },

        existingOverlappedTimePeriods(state, getters) {
            return getters.overlappedTimePeriods.filter(timePeriod => !!timePeriod.id);
        },

        invalidTimePeriods(state) {
            return state.timePeriods.filter(timePeriod => timePeriod.from.isSameOrAfter(timePeriod.to));
        },

        overlappedTimePeriods(state, getters) {
            const overlapped = [];

            const ordered = state.timePeriods.sort(
                (a, b) => a[getters.fromField].valueOf() - b[getters.fromField].valueOf()
            );

            let previousTimePeriod = first(ordered);

            ordered.slice(1).forEach(timePeriod => {
                const rangeA = moment.range(previousTimePeriod[getters.fromField], previousTimePeriod[getters.toField]);
                const rangeB = moment.range(timePeriod[getters.fromField], timePeriod[getters.toField]);

                if (
                    timePeriod.enrollment_group_id === previousTimePeriod.enrollment_group_id &&
                    ((rangeA.overlaps(rangeB) && !rangeA.end.isSame(rangeB.start)) ||
                        rangeA.contains(rangeB) ||
                        rangeB.contains(rangeA))
                ) {
                    overlapped.push(timePeriod, previousTimePeriod);
                }

                previousTimePeriod = timePeriod;
            });

            return uniqBy(overlapped, 'id');
        },

        fromField(state) {
            return state.courseKeyEnabled ? 'window_from' : 'from';
        },

        toField(state) {
            return state.courseKeyEnabled ? 'window_to' : 'to';
        },

        checkInWindow(state) {
            return state.courseKeyEnabled ? state.checkInWindow : 0;
        },

        checkOutWindow(state) {
            return state.courseKeyEnabled ? state.checkOutWindow : 0;
        },
    },

    actions: {
        init({ commit, getters, state }, timePeriods) {
            commit(
                'SET_TIME_PERIODS',
                timePeriods.map(timePeriod => {
                    return {
                        id: timePeriod.id,
                        from: timePeriod.timestamp('from'),
                        to: timePeriod.timestamp('to'),
                        window_from: timePeriod.timestamp('from').clone().subtract(getters.checkInWindow, 'minutes'),
                        window_to: timePeriod.timestamp('to').clone().add(getters.checkOutWindow, 'minutes'),
                        enrollment_group_id: timePeriod.group('id'),
                        display_week_number: timePeriod.section.display_week_number,
                    };
                })
            );
        },

        initTemplates({ commit, getters }, timePeriodTemplates) {
            const pseudoCourseStart = moment.utc().day(1);

            commit(
                'SET_TIME_PERIODS',
                timePeriodTemplates.map(timePeriodTemplate => {
                    const weekNumber = timePeriodTemplate.section_template.section_settings_template.week_number;

                    const from = pseudoCourseStart
                        .clone()
                        .add(weekNumber - 1, 'weeks')
                        .add(timePeriodTemplate.from_day - 1, 'days')
                        .setTimeFromString(timePeriodTemplate.from_time);

                    const to = pseudoCourseStart
                        .clone()
                        .add(weekNumber - 1, 'weeks')
                        .add(timePeriodTemplate.to_day - 1, 'days')
                        .setTimeFromString(timePeriodTemplate.to_time);

                    return {
                        id: timePeriodTemplate.id,
                        enrollment_group_id: null,
                        from,
                        to,
                        window_from: from.clone().subtract(getters.checkInWindow, 'minutes'),
                        window_to: to.clone().add(getters.checkOutWindow, 'minutes'),
                        display_week_number: timePeriodTemplate.section_template.display_week_number,
                    };
                })
            );
        },

        addNewTimePeriods({ commit }, timePeriods) {
            commit('ADD_NEW_TIME_PERIODS', timePeriods);
        },

        addNewTimePeriodTemplates({ commit }, timePeriods) {
            commit('ADD_NEW_TIME_PERIODS', timePeriods);
        },

        update({ commit, dispatch, getters }, { form, timePeriod }) {
            commit('SET_EDITING_TIME_PERIOD', timePeriod);

            commit('UPDATE_TIME_PERIOD', {
                id: timePeriod.id,
                enrollment_group_id: form.enrollment_group_id,
                from: moment(form.from),
                to: moment(form.to),
                window_from: moment(form.from).subtract(getters.checkInWindow, 'minutes'),
                window_to: moment(form.to).add(getters.checkOutWindow, 'minutes'),
                display_week_number: timePeriod.section.display_week_number,
            });
        },

        bulkUpdate({ commit, dispatch, getters }, { form, timePeriods }) {
            timePeriods.forEach(timePeriod => {
                const originalFrom = timePeriod.timestamp('from');
                const originalTo = timePeriod.timestamp('to');
                let from = timePeriod.timestamp('from');
                let to = timePeriod.timestamp('to');
                const weekStart = timePeriod.section.section_settings.timestamp('week_start');

                if (form.apply_from_day) {
                    from = weekStart.clone().add(form.from_day - 1, 'days');
                }

                if (form.apply_from_time) {
                    from.hour(form.from_time.getHours()).minute(form.from_time.getMinutes());
                } else {
                    from.hour(originalFrom.hour()).minute(originalFrom.minutes()).second(originalFrom.seconds());
                }

                if (form.apply_to_day) {
                    to = weekStart.clone().add(form.to_day - 1, 'days');
                }

                if (form.apply_to_time) {
                    to.hour(form.to_time.getHours()).minute(form.to_time.getMinutes());
                } else {
                    to.hour(originalTo.hour()).minute(originalTo.minutes()).second(originalTo.seconds());
                }

                commit('UPDATE_TIME_PERIOD', {
                    id: timePeriod.id,
                    enrollment_group_id: timePeriod.group('id'),
                    from,
                    to,
                    window_from: from.clone().subtract(getters.checkInWindow, 'minutes'),
                    window_to: to.clone().add(getters.checkOutWindow, 'minutes'),
                    display_week_number: timePeriod.section.display_week_number,
                });
            });
        },

        updateTemplate({ commit, dispatch, getters }, { form, timePeriodTemplate }) {
            const pseudoCourseStart = moment.utc().day(1);

            const sectionTemplate = SectionTemplate.query()
                .with('section_settings_template')
                .find(form.section_template_id);

            const weekNumber = sectionTemplate.section_settings_template.week_number;
            const displayWeekNumber = sectionTemplate.display_week_number;

            commit('SET_EDITING_TIME_PERIOD', timePeriodTemplate);

            const from = pseudoCourseStart
                .clone()
                .add(weekNumber - 1, 'weeks')
                .add(form.from_day - 1, 'days')
                .hour(form.from_time.getHours())
                .minute(form.from_time.getMinutes());

            const to = pseudoCourseStart
                .clone()
                .add(weekNumber - 1, 'weeks')
                .add(form.to_day - 1, 'days')
                .hour(form.to_time.getHours())
                .minute(form.to_time.getMinutes());

            commit('UPDATE_TIME_PERIOD', {
                id: timePeriodTemplate.id,
                enrollment_group_id: null,
                from,
                to,
                window_from: from.clone().subtract(getters.checkInWindow, 'minutes'),
                window_to: to.clone().add(getters.checkOutWindow, 'minutes'),
                display_week_number: displayWeekNumber,
            });
        },

        bulkUpdateTemplates({ commit, dispatch, getters }, { form, timePeriodTemplates }) {
            timePeriodTemplates.forEach(timePeriodTemplate => {
                const currentTimePeriodTemplate = getters.findById(timePeriodTemplate.id);

                let { from, to } = currentTimePeriodTemplate;

                const weekStart = currentTimePeriodTemplate.from.clone().day(1).startOf('day');

                if (form.apply_from_day) {
                    from = weekStart.clone().add(form.from_day - 1, 'days');
                }

                if (form.apply_from_time) {
                    from.hour(form.from_time.getHours()).minute(form.from_time.getMinutes());
                } else {
                    from.hour(currentTimePeriodTemplate.from.hour()).minute(currentTimePeriodTemplate.from.minutes());
                }

                if (form.apply_to_day) {
                    to = weekStart.clone().add(form.to_day - 1, 'days');
                }

                if (form.apply_to_time) {
                    to.hour(form.to_time.getHours()).minute(form.to_time.getMinutes());
                } else {
                    to.hour(currentTimePeriodTemplate.to.hour()).minute(currentTimePeriodTemplate.to.minutes());
                }

                commit('UPDATE_TIME_PERIOD', {
                    id: timePeriodTemplate.id,
                    enrollment_group_id: null,
                    from,
                    to,
                    window_from: from.clone().subtract(getters.checkInWindow, 'minutes'),
                    window_to: to.clone().add(getters.checkOutWindow, 'minutes'),
                    display_week_number: timePeriodTemplate.section_template.display_week_number,
                });
            });
        },

        updateTimePeriodTemplate({ commit }, timePeriodTemplate) {},

        clearNewTimePeriods({ commit }) {
            commit('CLEAR_NEW_TIME_PERIODS');
        },
    },

    mutations: {
        SET_TIME_PERIODS(state, timePeriods) {
            state.timePeriods = timePeriods;
        },

        ADD_NEW_TIME_PERIODS(state, timePeriods) {
            state.timePeriods = [...state.timePeriods, ...timePeriods];
        },

        UPDATE_TIME_PERIOD(state, { id, enrollment_group_id, from, to, display_week_number }) {
            state.timePeriods = state.timePeriods.map(timePeriod => {
                if (timePeriod.id === id) {
                    return {
                        id,
                        enrollment_group_id,
                        from,
                        to,
                        window_from: from.clone().subtract(state.checkInWindow, 'minutes'),
                        window_to: to.clone().add(state.checkOutWindow, 'minutes'),
                        display_week_number,
                    };
                }

                return timePeriod;
            });
        },

        CLEAR_NEW_TIME_PERIODS(state) {
            state.timePeriods = state.timePeriods.filter(timePeriod => timePeriod.id !== 0);
        },

        SET_EDITING_TIME_PERIOD(state, timePeriod) {
            state.editingTimePeriodId = timePeriod.id;
        },

        SET_COURSE_KEY_ENABLED(state, enabled) {
            state.courseKeyEnabled = enabled;
        },

        SET_COURSE_KEY_SETTINGS(state, { course_key_enabled, check_in_window, check_out_window }) {
            state.courseKeyEnabled = course_key_enabled;
            state.checkInWindow = check_in_window;
            state.checkOutWindow = check_out_window;
        },
    },
};
